import React from 'react';
import { useCart } from '../components/CartContext';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    Box, 
    Divider,
    IconButton,
    Chip,
    Stack,
    Card,
    CardContent,
    Fade,
    Grow
} from '@mui/material';
import {
    Delete as DeleteIcon,
    ShoppingCart as ShoppingCartIcon,
    ArrowForward as ArrowForwardIcon,
    LocalShipping as LocalShippingIcon,
    School as SchoolIcon,
    MenuBook as MenuBookIcon,
    Cloud as CloudIcon,
    AutoAwesome as AutoAwesomeIcon,
    RemoveShoppingCart as EmptyCartIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Cart = () => {
    const { cart, removeFromCart } = useCart();
    const navigate = useNavigate();

    const handleRemoveFromCart = (productId) => {
        removeFromCart(productId);
    };

    const calculateSubtotal = () => {
        return cart ? cart.reduce((total, product) => total + product.price * (product.quantity || 1), 0) : 0;
    };

    const calculateTax = () => {
        const taxableItems = cart ? cart.filter(item => item.isTaxable) : [];
        const taxableTotal = taxableItems.reduce((total, product) => total + product.price * (product.quantity || 1), 0);
        return taxableTotal * 0.08; // 8% tax rate
    };

    const calculateTotal = () => {
        return calculateSubtotal() + calculateTax();
    };

    const getOptionIcon = (selectedOption) => {
        switch (selectedOption) {
            case 'studyProgramOnly':
                return <CloudIcon sx={{ fontSize: 20 }} />;
            case 'studyProgramWithBooks':
                return <AutoAwesomeIcon sx={{ fontSize: 20 }} />;
            case 'booksOnly':
                return <MenuBookIcon sx={{ fontSize: 20 }} />;
            default:
                return <SchoolIcon sx={{ fontSize: 20 }} />;
        }
    };

    const getOptionLabel = (selectedOption) => {
        switch (selectedOption) {
            case 'studyProgramOnly':
                return 'Digital Access';
            case 'studyProgramWithBooks':
                return 'Complete Package';
            case 'booksOnly':
                return 'Books Only';
            default:
                return selectedOption;
        }
    };

    const getOptionColor = (selectedOption) => {
        switch (selectedOption) {
            case 'studyProgramOnly':
                return 'primary';
            case 'studyProgramWithBooks':
                return 'success';
            case 'booksOnly':
                return 'secondary';
            default:
                return 'default';
        }
    };

    if (!cart || cart.length === 0) {
        return (
            <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
                <Fade in timeout={800}>
                    <Paper 
                        elevation={0}
                        sx={{ 
                            textAlign: 'center', 
                            py: 8, 
                            px: 4,
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            borderRadius: 3
                        }}
                    >
                        <EmptyCartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Your Cart is Empty
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                            Looks like you haven't added any study materials yet. Browse our comprehensive exam preparation packages to get started.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/shop')}
                            sx={{
                                background: 'linear-gradient(45deg, #ff5e1c 30%, #ff8c00 90%)',
                                px: 4,
                                py: 1.5,
                                borderRadius: 2,
                                boxShadow: '0 4px 20px rgba(255, 94, 28, 0.3)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 25px rgba(255, 94, 28, 0.4)'
                                }
                            }}
                        >
                            Start Shopping
                        </Button>
                    </Paper>
                </Fade>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                    variant="h3" 
                    sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #ff5e1c 30%, #ff8c00 90%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    Shopping Cart
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {cart.length} item{cart.length !== 1 ? 's' : ''} in your cart
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
                {/* Cart Items */}
                <Box sx={{ flex: 1 }}>
                    <Stack spacing={2}>
                        {cart.map((product, index) => (
                            <Grow in timeout={500 + index * 100} key={product.cartItemId || product._id}>
                                <Paper 
                                    elevation={2}
                                    sx={{ 
                                        p: 3,
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            elevation: 4,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                        {/* Product Icon */}
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: 2,
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                flexShrink: 0
                                            }}
                                        >
                                            {getOptionIcon(product.selectedOption)}
                                        </Box>

                                        {/* Product Details */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                                {product.name}
                                            </Typography>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <Chip
                                                    icon={getOptionIcon(product.selectedOption)}
                                                    label={getOptionLabel(product.selectedOption)}
                                                    color={getOptionColor(product.selectedOption)}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                {product.isTaxable && (
                                                    <Chip
                                                        label="Taxable"
                                                        size="small"
                                                        variant="outlined"
                                                        color="warning"
                                                    />
                                                )}
                                                {(product.selectedOption === 'studyProgramWithBooks' || product.selectedOption === 'booksOnly') && (
                                                    <Chip
                                                        icon={<LocalShippingIcon />}
                                                        label="Ships Physical"
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Price and Actions */}
                                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                                                ${product.price}
                                            </Typography>
                                            
                                            <IconButton
                                                onClick={() => handleRemoveFromCart(product.cartItemId || product._id)}
                                                sx={{
                                                    color: 'error.main',
                                                    '&:hover': {
                                                        bgcolor: 'error.light',
                                                        color: 'white'
                                                    }
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grow>
                        ))}
                    </Stack>
                </Box>

                {/* Order Summary */}
                <Box sx={{ width: { xs: '100%', lg: 400 }, flexShrink: 0 }}>
                    <Paper 
                        elevation={3}
                        sx={{ 
                            p: 3, 
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            position: 'sticky',
                            top: 24
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                            Order Summary
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body1">Subtotal</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    ${calculateSubtotal().toFixed(2)}
                                </Typography>
                            </Box>
                            
                            {calculateTax() > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body1" color="text.secondary">Tax (8%)</Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        ${calculateTax().toFixed(2)}
                                    </Typography>
                                </Box>
                            )}
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Total</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    ${calculateTotal().toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate('/checkout')}
                            sx={{
                                background: 'linear-gradient(45deg, #ff5e1c 30%, #ff8c00 90%)',
                                py: 1.5,
                                borderRadius: 2,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: '0 4px 20px rgba(255, 94, 28, 0.3)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 25px rgba(255, 94, 28, 0.4)'
                                }
                            }}
                        >
                            Proceed to Checkout
                        </Button>

                        <Button
                            fullWidth
                            variant="text"
                            onClick={() => navigate('/shop')}
                            sx={{ mt: 2, color: 'text.secondary' }}
                        >
                            Continue Shopping
                        </Button>
                    </Paper>
                </Box>
            </Box>
        </Container>
    );
};

export default Cart;
